import { clamp } from '@racemap/utilities/math';
import type { EChartsOption, XAXisComponentOption, YAXisComponentOption } from 'echarts';
import ReactECharts, { type EChartsInstance } from 'echarts-for-react';
import { useCallback, useMemo, useRef } from 'react';

export type XAxisOption<T> = XAXisComponentOption & {
  dataKey: keyof T;
};
export type YAxisOption<T> = YAXisComponentOption & {
  dataKey: keyof T;
};
export type ChartOptions = Partial<EChartsOption>;
export type Series = EChartsOption['series'];
export type DataEntry = Record<string, string | number | Date>;
interface DataZoomProps {
  start?: number;
  end?: number;
  startValue?: number;
  endValue?: number;
  type: string;
}

type ChartEventHandler = (...args: Array<unknown>) => void;
type ChartEventHandlers = Record<string, ChartEventHandler>;

export function XYAreachart<T extends DataEntry>({
  series,
  xAxisOption,
  yAxisOption,
  title,
  onDataZoomChange,
  chartOptions,
  showLoading,
  onEvents,
}: {
  series: Series;
  xAxisOption: XAxisOption<T>;
  yAxisOption: YAxisOption<T>;
  title?: string;
  onDataZoomChange?: (start: number | null, end: number | null) => void;
  chartOptions?: ChartOptions;
  showLoading?: boolean;
  onEvents?: ChartEventHandlers;
}) {
  const option = useMemo(
    () => getOption(title || 'AreaChart', series, xAxisOption, yAxisOption, chartOptions),
    [title, series, xAxisOption, yAxisOption, chartOptions],
  );

  // Keep a ref to firstSerie so the stable dataZoom callback can read the latest value
  // without needing to be recreated when series changes.
  const firstSerieRef = useRef(Array.isArray(series) ? series?.[0] : series);
  firstSerieRef.current = Array.isArray(series) ? series?.[0] : series;

  const handleChartReady = (chartInstance: EChartsInstance) => {
    // echarts-for-react's EChartsInstance types getOption() as unknown/{}; the runtime shape is
    // still an array of the option's own dataZoom entries.
    const dataZoom = chartInstance.getOption().dataZoom as
      | Array<{ startValue: number; endValue: number }>
      | undefined;
    if (dataZoom == null || dataZoom.length === 0) return;
    const first = dataZoom[0];
    chartInstance.dispatchAction({
      type: 'dataZoom',
      batch: [{ startValue: first.startValue, endValue: first.endValue, type: 'dataZoom' }],
    });
  };

  // Stable callback — only recreated when onDataZoomChange identity changes.
  // Reads firstSerie via ref to avoid stale closure without adding series to deps.
  const handleDataZoom = useCallback(
    (
      props:
        | DataZoomProps
        | {
            batch: Array<DataZoomProps>;
          },
    ) => {
      const actionData = 'batch' in props ? props.batch : [props];
      const { start, startValue, end, endValue, type } = actionData[0];

      if (type === 'datazoom' && onDataZoomChange) {
        if (start === 0 && end === 100) {
          onDataZoomChange(null, null);
        } else if (startValue != null && endValue != null) {
          onDataZoomChange(startValue, endValue);
        } else if (start != null && end != null && firstSerieRef.current != null) {
          const firstSerie = firstSerieRef.current;
          const maxIndex = Array.isArray(firstSerie?.data) ? firstSerie.data.length - 1 : 0;
          const startIndex = clamp(0, Math.floor((start / 100) * maxIndex), maxIndex);
          const endIndex = clamp(0, Math.floor((end / 100) * maxIndex), maxIndex);

          const sv = (firstSerie?.data as Record<number, Array<number>> | undefined)?.[
            startIndex
          ]?.[0];
          const ev = (firstSerie?.data as Record<number, Array<number>> | undefined)?.[
            endIndex
          ]?.[0];

          if (sv == null || ev == null) return;
          onDataZoomChange(sv, ev);
        }
      }
    },
    [onDataZoomChange],
  );

  // Memoize mergedEvents so echarts-for-react doesn't see a new onEvents reference on every
  // render. When onEvents changes (dispose + reinit in echarts-for-react), that destroys
  // internal chart state including the zoom position.
  const mergedEvents: ChartEventHandlers = useMemo(
    () => ({
      ...onEvents,
      dataZoom: (...args: Array<unknown>) => {
        const [firstArg] = args;
        if (firstArg != null) {
          handleDataZoom(firstArg as DataZoomProps | { batch: Array<DataZoomProps> });
        }
        onEvents?.dataZoom?.(...args);
      },
    }),
    [onEvents, handleDataZoom],
  );

  return (
    <ReactECharts
      option={option}
      style={{ width: '100%', height: '100%', minHeight: '300px' }}
      lazyUpdate={true}
      onEvents={mergedEvents}
      onChartReady={handleChartReady}
      showLoading={showLoading}
    />
  );
}

const getOption = <T extends DataEntry>(
  title: string,
  series: EChartsOption['series'],
  xAxis: XAxisOption<T>,
  yAxis: YAxisOption<T>,
  chartOptions?: ChartOptions,
): EChartsOption => {
  return {
    name: title,
    label: title,
    grid: {
      show: true,
    },
    xAxis: {
      type: 'time',
      ...xAxis,
    },
    animation: false,
    yAxis: {
      type: 'value',
      ...yAxis,
    },
    tooltip: {
      trigger: 'axis',
    },
    series,
    ...chartOptions,
  };
};
